import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// SPDX-License-Identifier: AGPL-3.0-or-later
import { forwardRef } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
/**
 * Dialog primitive — a thin compose over `@radix-ui/react-dialog`. Radix
 * owns the hard parts (focus trap, Escape close, aria-modal, body scroll
 * lock, focus restoration) so we don't reimplement them. We add the
 * design-system shell (backdrop, panel, footer slot) and a labelled
 * close button so consumers don't have to wire it themselves.
 *
 * Composition:
 *   <Dialog>
 *     <Dialog.Trigger>Open</Dialog.Trigger>
 *     <Dialog.Content>
 *       <Dialog.Title>Title</Dialog.Title>
 *       <Dialog.Description>Body text</Dialog.Description>
 *       <Dialog.Footer>
 *         <Dialog.Close asChild><Button>Cancel</Button></Dialog.Close>
 *         <Button onClick={confirm}>Confirm</Button>
 *       </Dialog.Footer>
 *     </Dialog.Content>
 *   </Dialog>
 */
const DialogRoot = DialogPrimitive.Root;
const DialogTrigger = DialogPrimitive.Trigger;
const DialogPortal = DialogPrimitive.Portal;
const DialogClose = DialogPrimitive.Close;
const DialogOverlay = forwardRef(function DialogOverlay(props, ref) {
    return _jsx(DialogPrimitive.Overlay, { ref: ref, "data-dialog-overlay": "true", ...props });
});
const DialogContent = forwardRef(function DialogContent(props, ref) {
    const { children, width = '28rem', showClose = true, style, ...rest } = props;
    return (_jsxs(DialogPortal, { children: [_jsx(DialogOverlay, {}), _jsxs(DialogPrimitive.Content, { ref: ref, "data-dialog-content": "true", style: { maxWidth: width, ...style }, ...rest, children: [children, showClose ? (_jsx(DialogPrimitive.Close, { "data-dialog-close": "true", "aria-label": "Close dialog", children: _jsx(X, { "aria-hidden": "true", size: 16 }) })) : null] })] }));
});
const DialogTitle = forwardRef(function DialogTitle(props, ref) {
    return _jsx(DialogPrimitive.Title, { ref: ref, "data-dialog-title": "true", ...props });
});
const DialogDescription = forwardRef(function DialogDescription(props, ref) {
    return _jsx(DialogPrimitive.Description, { ref: ref, "data-dialog-description": "true", ...props });
});
function DialogFooter(props) {
    return _jsx("div", { "data-dialog-footer": "true", children: props.children });
}
function DialogBody(props) {
    return _jsx("div", { "data-dialog-body": "true", children: props.children });
}
const DialogFn = DialogRoot;
DialogFn.Trigger = DialogTrigger;
DialogFn.Content = DialogContent;
DialogFn.Title = DialogTitle;
DialogFn.Description = DialogDescription;
DialogFn.Footer = DialogFooter;
DialogFn.Body = DialogBody;
DialogFn.Close = DialogClose;
export const Dialog = DialogFn;
//# sourceMappingURL=Dialog.js.map